import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { reportsService } from '../services'

/**
 * Browser-detected IANA timezone string, resolved once at module load.
 * Used to tell the backend which local timezone the admin is sitting in so
 * all chart day/week boundaries match what the admin sees on their clock.
 * Falls back to 'Asia/Dhaka' (server default) if the Intl API is unavailable.
 *
 * Some browsers report deprecated names (e.g. Asia/Calcutta) that PostgreSQL
 * rejects in AT TIME ZONE — normalize those before sending to the API.
 */
const BROWSER_TIMEZONE_ALIASES = {
  'Asia/Calcutta': 'Asia/Kolkata',
  'Asia/Chungking': 'Asia/Shanghai',
  'Asia/Katmandu': 'Asia/Kathmandu',
  'Asia/Rangoon': 'Asia/Yangon',
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Europe/Kiev': 'Europe/Kyiv',
}
const RAW_VIEWER_TIMEZONE =
  (typeof Intl !== 'undefined' &&
    Intl.DateTimeFormat().resolvedOptions().timeZone) ||
  'Asia/Dhaka'
export const VIEWER_TIMEZONE =
  BROWSER_TIMEZONE_ALIASES[RAW_VIEWER_TIMEZONE] || RAW_VIEWER_TIMEZONE

export const parseDashboardIdList = (value) => [
  ...new Set(
    String(value || '')
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean),
  ),
]

export const joinDashboardIds = (options = []) => [
  ...new Set(
    options
      .map((option) => String(option?.id ?? option ?? ''))
      .filter(Boolean),
  ),
].join(',')

const DEFAULT_FILTERS = {
  globalPeriod: '7d',
  locationIds: '',
  categoryIds: '',
  organizationIds: '',
  salesRange: '7d',
  outletRange: '7d',
  partnerRange: '7d',
  categoryPeriod: '7d',
  comparisonPeriod: 'week',
  stockView: 'category',
  expiryWindow: '30',
  fastPeriod: '7d',
  heatmapWeek: 'this',
  pendingFilter: 'all',
}

const GLOBAL_FILTER_KEYS = [
  'locationIds',
  'categoryIds',
  'organizationIds',
  'globalPeriod',
  'globalStart',
  'globalEnd',
]

const SECTION_OVERRIDE_CLEAR = {
  locationIds: ['categoryOutletId', 'heatmapOutletId'],
  categoryIds: ['expiryCategoryId', 'fastCategoryId'],
}

const DASHBOARD_SECTIONS = [
  'executiveOverview',
  'salesOverview',
  'topOutlets',
  'topPartnerShops',
  'orderStatus',
  'categorySales',
  'salesComparison',
  'stockHealth',
  'pendingOrders',
  'expiryRisk',
  'fastMovers',
  'peakHoursHeatmap',
]

export function useDashboard() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [sectionLoading, setSectionLoading] = useState({})
  const [error, setError] = useState(null)
  const [updatedAt, setUpdatedAt] = useState(null)
  const globalAbortRef = useRef(null)
  const sectionAbortRefs = useRef({})
  const filtersRef = useRef({})
  const globalEpochRef = useRef(0)
  const sectionEpochRef = useRef({})
  const globalLoadingRef = useRef(false)

  const clearDashboardSectionLoading = useCallback((prev) => {
    const next = { ...prev }
    DASHBOARD_SECTIONS.forEach((key) => {
      next[key] = false
    })
    return next
  }, [])

  const filters = useMemo(
    () => ({
      locationIds: searchParams.get('locationIds') || '',
      categoryIds: searchParams.get('categoryIds') || '',
      organizationIds: searchParams.get('organizationIds') || '',
      // Keep global period aligned with salesRange when the URL only has section ranges
      // (avoids sending globalPeriod=7d while salesRange=quarter, which locked KPIs to 7d).
      globalPeriod:
        searchParams.get('globalPeriod') ||
        searchParams.get('salesRange') ||
        DEFAULT_FILTERS.globalPeriod,
      globalStart: searchParams.get('globalStart') || '',
      globalEnd: searchParams.get('globalEnd') || '',
      salesRange:
        searchParams.get('salesRange') ||
        searchParams.get('globalPeriod') ||
        DEFAULT_FILTERS.salesRange,
      salesStart: searchParams.get('salesStart') || '',
      salesEnd: searchParams.get('salesEnd') || '',
      outletRange: searchParams.get('outletRange') || DEFAULT_FILTERS.outletRange,
      partnerRange: searchParams.get('partnerRange') || DEFAULT_FILTERS.partnerRange,
      categoryPeriod: searchParams.get('categoryPeriod') || DEFAULT_FILTERS.categoryPeriod,
      comparisonPeriod: searchParams.get('comparisonPeriod') || DEFAULT_FILTERS.comparisonPeriod,
      stockView: searchParams.get('stockView') || DEFAULT_FILTERS.stockView,
      expiryWindow: searchParams.get('expiryWindow') || DEFAULT_FILTERS.expiryWindow,
      fastPeriod: searchParams.get('fastPeriod') || DEFAULT_FILTERS.fastPeriod,
      heatmapWeek: searchParams.get('heatmapWeek') || DEFAULT_FILTERS.heatmapWeek,
      pendingFilter: searchParams.get('pendingFilter') || DEFAULT_FILTERS.pendingFilter,
      categoryOutletId: searchParams.get('categoryOutletId') || '',
      expiryCategoryId: searchParams.get('expiryCategoryId') || '',
      fastCategoryId: searchParams.get('fastCategoryId') || '',
      heatmapOutletId: searchParams.get('heatmapOutletId') || '',
    }),
    [searchParams],
  )

  filtersRef.current = filters

  const globalFilterKey = useMemo(
    () => GLOBAL_FILTER_KEYS.map((k) => filters[k] || '').join('|'),
    [filters],
  )

  const buildParams = useCallback((section = 'all', extra = {}, sourceFilters = filtersRef.current) => {
    // viewerTimezone is always injected — not a URL param, so it won't appear in sourceFilters.
    const params = { section, viewerTimezone: VIEWER_TIMEZONE, pendingLimit: 5, ...extra }
    Object.entries(sourceFilters).forEach(([key, value]) => {
      if (value !== '' && value != null) params[key] = value
    })
    return params
  }, [])

  const isStaleResponse = useCallback((section, epoch) => {
    if (section === 'all') return epoch !== globalEpochRef.current
    return epoch !== sectionEpochRef.current[section]
  }, [])

  const fetchDashboard = useCallback(async (section = 'all', extra = {}) => {
    const controller = new AbortController()
    let epoch

    if (section === 'all') {
      globalEpochRef.current += 1
      epoch = globalEpochRef.current
      Object.keys(sectionEpochRef.current).forEach((key) => {
        sectionEpochRef.current[key] = (sectionEpochRef.current[key] || 0) + 1
      })
      globalAbortRef.current?.abort()
      Object.values(sectionAbortRefs.current).forEach((pending) => pending?.abort?.())
      sectionAbortRefs.current = {}
      globalAbortRef.current = controller
      globalLoadingRef.current = true
      setSectionLoading(Object.fromEntries(DASHBOARD_SECTIONS.map((s) => [s, true])))
      setData((prev) =>
        prev
          ? {
              ...prev,
              sectionErrors: {},
            }
          : prev,
      )
    } else {
      // Global reload already covers every section. Skip section fetches while it
      // is in flight so period Apply does not abort the full dashboard request.
      if (globalLoadingRef.current) {
        return null
      }
      const preserveGlobalLoad = section === 'executiveDetail'
      if (!preserveGlobalLoad) {
        globalAbortRef.current?.abort()
        setLoading(false)
        globalLoadingRef.current = false
        // Global fetch sets every section to loading; if we abort it for a section
        // refresh, clear those stale flags (orderStatus only loads via global).
        setSectionLoading({})
      }
      sectionAbortRefs.current[section]?.abort()
      sectionAbortRefs.current[section] = controller
      sectionEpochRef.current[section] = (sectionEpochRef.current[section] || 0) + 1
      epoch = sectionEpochRef.current[section]
    }

    if (section === 'all') setLoading(true)
    else setSectionLoading((s) => ({ ...s, [section]: true }))

    try {
      setError(null)
      const response = await reportsService.getExecutiveDashboard({
        ...buildParams(section, extra),
        signal: controller.signal,
      })
      if (controller.signal.aborted || isStaleResponse(section, epoch)) return null

      setUpdatedAt(new Date(response.generatedAt || Date.now()))
      setData((prev) => ({
        ...(prev || {}),
        timezone: response.timezone,
        timezoneSource: response.timezoneSource,
        filters: response.filters,
        filterOptions:
          response.filterOptions ||
          response.sections?.filterOptions ||
          prev?.filterOptions,
        sectionErrors:
          section === 'all'
            ? response.sectionErrors || {}
            : {
                ...Object.fromEntries(
                  Object.entries(prev?.sectionErrors || {}).filter(
                    ([key]) => key !== section,
                  ),
                ),
                ...(response.sectionErrors || {}),
              },
        sections:
          section === 'all'
            ? response.sections || {}
            : {
                ...(prev?.sections || {}),
                ...(response.sections || {}),
              },
      }))
      return response
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return null
      if (isStaleResponse(section, epoch)) return null
      console.error('Dashboard fetch failed:', err)
      const message = err?.response?.data?.message || 'Failed to load dashboard'
      if (section === 'all') {
        setError(message)
        return { error: message }
      }
      setData((prev) => ({
        ...(prev || {}),
        sectionErrors: {
          ...(prev?.sectionErrors || {}),
          [section]: message,
        },
      }))
      return { error: message }
    } finally {
      if (controller.signal.aborted) return
      if (isStaleResponse(section, epoch)) return
      if (section === 'all') {
        setLoading(false)
        globalLoadingRef.current = false
        setSectionLoading(clearDashboardSectionLoading)
      } else {
        setSectionLoading((s) => ({ ...s, [section]: false }))
      }
    }
  }, [buildParams, isStaleResponse, clearDashboardSectionLoading])

  // Full reload only when global filters change — not section pill/dropdown changes.
  useEffect(() => {
    fetchDashboard('all')
    return () => {
      globalAbortRef.current?.abort()
      Object.values(sectionAbortRefs.current).forEach((pending) => pending?.abort?.())
    }
  }, [globalFilterKey, fetchDashboard])

  const setFilter = useCallback(
    (updates) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        Object.entries(updates).forEach(([key, value]) => {
          if (Object.prototype.hasOwnProperty.call(SECTION_OVERRIDE_CLEAR, key)) {
            SECTION_OVERRIDE_CLEAR[key].forEach((overrideKey) => next.delete(overrideKey))
          }
        })
        if (Object.prototype.hasOwnProperty.call(updates, 'locationIds')) {
          next.delete('organizationIds')
        }
        Object.entries(updates).forEach(([key, value]) => {
          if (value === '' || value == null) next.delete(key)
          else if (['locationIds', 'categoryIds', 'organizationIds'].includes(key)) {
            const deduped = [
              ...new Set(
                String(value)
                  .split(',')
                  .map((part) => part.trim())
                  .filter(Boolean),
              ),
            ].join(',')
            if (deduped) next.set(key, deduped)
            else next.delete(key)
          } else next.set(key, String(value))
        })
        return next
      }, { replace: true })
    },
    [setSearchParams],
  )

  const refreshSection = useCallback(
    (section, extra) => fetchDashboard(section, extra),
    [fetchDashboard],
  )

  return {
    data,
    loading,
    sectionLoading,
    error,
    updatedAt,
    filters,
    viewerTimezone: VIEWER_TIMEZONE,
    setFilter,
    refreshAll: () => fetchDashboard('all'),
    refreshSection,
  }
}
